<script lang="ts">
  import { getDialog } from '$lib/dialogs';
  import { flipper, formDataToObject, logError } from '$lib/utils';
  import { writable } from 'svelte/store';
  import Alert from '../Alert.svelte';
  import Spinner from '../Spinner.svelte';
  import { session } from '$lib/stores';
  import { SignupSchema } from '$lib/schema';
  import { APP_AUTH } from '$lib/constants';
  import { invalidate } from '$app/navigation';
  import RegionDropdown from '../RegionDropdown.svelte';
  import z from 'zod';
  import { browserSupportsWebAuthn, startRegistration } from '@simplewebauthn/browser';

  const dialog = getDialog();
  const status = writable<'loading' | 'success' | null>(null);
  const errors = writable<Record<string, any> | null>(null);

  $: if ($session) dialog.set(null);

  const errorClass =
    'border-red-300 text-red-900 placeholder-red-300 focus:outline-none focus:ring-red-500 focus:border-red-500';
  const inputClass = (error?: any) =>
    `form-input shadow-sm primary-500 focus:ring-primary-500 focus:border-primary-500 block w-full sm:text-sm border-gray-300 rounded-md ${
      error ? errorClass : ''
    }}`;

  const passkeyRegistration = async (user: z.infer<typeof SignupSchema>) => {
    if (!flipper('webauthn') || !browserSupportsWebAuthn()) {
      return undefined;
    }

    // Get the options from the server
    const res = await fetch('/api/session/passkey/register/options?email=' + user.email, {
      headers: { 'Content-Type': 'application/json' }
    });

    try {
      if (res.ok) {
        const options = await res.json();
        return {
          ok: await startRegistration(options)
        };
      } else {
        return { error: 'webauthn' };
      }
    } catch (error) {
      logError(error);
      $errors = { webauthn: 'Error al registrar el dispositivo' };
      return { error: 'webauthn' };
    }
  };

  const onSubmit = async (e: Event) => {
    $errors = null;
    const form = e.target as HTMLFormElement;
    const data = formDataToObject(form);
    const validation = SignupSchema.safeParse(data);

    if (!validation.success) {
      $errors = Object.fromEntries(
        validation.error.issues.map((e) => [e.path.join('.'), e.message])
      );
      return;
    }

    $status = 'loading';

    const options = await passkeyRegistration(validation.data);
    if (options?.error) {
      $status = null;
      return;
    }

    const optionsInput = document.createElement('input');
    optionsInput.type = 'hidden';
    optionsInput.name = 'options';
    optionsInput.value = JSON.stringify(options?.ok || {});
    form.appendChild(optionsInput);

    const res = await fetch('/registrarse', {
      method: 'post',
      body: new FormData(form)
    });

    try {
      const data = await res.json();

      $status = null;

      if (data.status == 200) {
        invalidate(APP_AUTH);
        $status = 'success';
      } else if (data.errors) {
        $errors = data.errors;
      } else if (data.data) {
        try {
          const _data = JSON.parse(data.data);
          const errs: any[] = Object.entries(_data[0])
            .map(([key, i]: any) => (i > 0 ? [key, _data[i]] : null))
            .filter(Boolean);
          if (errs.length > 0) {
            $errors = Object.fromEntries(errs);
          } else {
            $errors = { unkown: 'Ocurrió un error' };
          }
        } catch (e) {
          logError(e);
          $errors = { unkown: 'Ocurrió un error' };
        }
      } else {
        logError(data);
        $errors = { unkown: 'Ocurrió un error' };
      }
    } catch (e) {
      logError(e);
      $errors = { unkown: (e as any).to_string() };
    }
  };
</script>

<form
  class={`space-y-8 divide-y divide-gray-200 ${$status == 'loading' ? 'animate-pulse' : ''}`}
  on:submit|preventDefault={onSubmit}
>
  <div>
    <h3 class="mb-6 text-center text-3xl font-extrabold text-primary-500" id="modal-title">
      Registrarse
    </h3>
    {#if $status == 'success'}
      <Alert severity="success">
        <p>Gracias por registrarse.</p>
        <p>Se ha enviado un correo electrónico de verificación.</p>
      </Alert>
    {:else}
      <div class="space-y-6">
        {#if $errors}
          <Alert severity="danger" messages={Object.values($errors)} />
        {/if}

        <div>
          <div class="mt-1 grid grid-cols-1 gap-y-2 gap-x-4 sm:grid-cols-6">
            <div class="sm:col-span-3">
              <label for="firstname" class="block text-sm font-medium text-gray-700">
                Nombre
              </label>
              <div class="mt-1">
                <input name="firstname" type="text" class={inputClass($errors?.['firstname'])} />
              </div>
            </div>

            <div class="sm:col-span-3">
              <label for="lastname" class="block text-sm font-medium text-gray-700">
                Apellido
              </label>
              <div class="mt-1">
                <input name="lastname" type="text" class={inputClass($errors?.['lastname'])} />
              </div>
            </div>

            <div class="sm:col-span-6">
              <label for="phone" class="block text-sm font-medium text-gray-700 sm:mt-px sm:pt-2">
                Teléfono
              </label>
              <div class="mt-1 sm:mt-0 sm:col-span-2">
                <div class="max-w-lg flex rounded-md shadow-sm">
                  <span
                    class={`inline-flex items-center px-3 rounded-l-md border border-r-0 border-gray-300 bg-gray-50 text-gray-500 sm:text-sm ${
                      $errors?.['phone'] ? errorClass : ''
                    }`}
                  >
                    +595
                  </span>
                  <input
                    type="number"
                    name="phone"
                    class={`form-input flex-1 block w-full focus:ring-primary-500 focus:border-primary-500 min-w-0 rounded-none rounded-r-md sm:text-sm border-gray-300 ${
                      $errors?.['phone'] ? errorClass : ''
                    }`}
                  />
                </div>
              </div>
            </div>

            <div class="sm:col-span-6">
              <label
                for="document_number"
                class="block text-sm font-medium text-gray-700 sm:mt-px sm:pt-2"
              >
                Cédula de identidad
              </label>
              <div class="mt-1">
                <input type="hidden" name="document_type" value="dni" />
                <input
                  name="document_number"
                  type="number"
                  class={inputClass($errors?.['document_number'])}
                />
              </div>
            </div>

            <div class="sm:col-span-6">
              <label for="address" class="block text-sm font-medium text-gray-700 sm:mt-px sm:pt-2">
                Dirección
              </label>
              <div class="mt-1">
                <input name="address" type="text" class={inputClass($errors?.['address'])} />
              </div>
            </div>

            <div class="sm:col-span-6">
              <label for="region_id" class="block text-sm font-medium text-gray-700">
                Departamento
              </label>
              <div class="mt-1">
                <RegionDropdown name="region_id" />
              </div>
            </div>

            <div class="sm:col-span-6">
              <label for="city" class="block text-sm font-medium text-gray-700">
                Localidad/Ciudad
              </label>
              <div class="mt-1">
                <input name="city" type="text" class={inputClass($errors?.['city'])} />
              </div>
            </div>

            <div class="sm:col-span-6">
              <label for="username" class="block text-sm font-medium text-gray-700">
                Nombre de usuario
              </label>
              <div class="mt-1">
                <input name="username" type="text" class={inputClass($errors?.['username'])} />
              </div>
              <div class="mt-1 text-gray-500 text-xs italic">
                El nombre de usuario será compartido públicamente cuando por ejemplo oferte un lote.
              </div>
            </div>

            <div class="sm:col-span-6">
              <label for="email" class="block text-sm font-medium text-gray-700">
                Dirección de correo electrónico
              </label>
              <div class="mt-1">
                <input name="email" type="email" class={inputClass($errors?.['email'])} />
              </div>
            </div>

            <div class="sm:col-span-6">
              <label for="password" class="block text-sm font-medium text-gray-700">
                Contraseña
              </label>
              <div class="mt-1">
                <input name="password" type="password" class={inputClass($errors?.['password'])} />
              </div>
            </div>

            <div class="relative flex gap-x-3 sm:col-span-6">
              <div class="flex h-6 items-center">
                <input
                  name="accepted_tos"
                  type="checkbox"
                  class={`h-4 w-4 rounded ${
                    $errors?.['accepted_tos']
                      ? errorClass
                      : 'border-gray-300 text-primary-500 focus:ring-primary-500'
                  }`}
                />
              </div>
              <div class="text-sm">
                <label for="accepted_tos" class="font-medium text-gray-900">
                  Acepto los <a
                    href="/terminos-y-condiciones-de-uso"
                    class="underline text-primary-500"
                    target="_blank"
                  >
                    Términos y condiciones
                  </a>
                </label>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="pt-5">
        <button
          type="submit"
          disabled={$status == 'loading' || Boolean($session)}
          class="
              w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm
              font-medium text-white bg-primary-500 hover:bg-primary-400 focus:outline-none focus:ring-2 focus:ring-offset-2
              focus:ring-primary-500
            "
        >
          {#if $status == 'loading' || Boolean($session)}<Spinner />{/if}
          Registrarse
        </button>
      </div>
    {/if}
  </div>
</form>
